import React, { useMemo, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { GenericFormPage } from '../../components/generic';
import {
  useClientInvoice,
  ClientInvoice,
  useSupportAgreementsForSelect,
  useInvoiceTypesForSelect,
} from './useData';
import {
  moduleConfig,
  getInitialFormData,
  mapItemToFormData,
} from './config';

const Form: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const isEdit = !!id;
  const { data: invoiceData } = useClientInvoice(isEdit ? parseInt(id!) : 0);

  const [selectedClientId, setSelectedClientId] = useState<number | undefined>(undefined);
  const { options: supportAgreementOptions } = useSupportAgreementsForSelect(selectedClientId);
  const { options: invoiceTypeOptions } = useInvoiceTypesForSelect();

  useEffect(() => {
    if (invoiceData?.clientId) {
      setSelectedClientId(invoiceData.clientId);
    } else if (!isEdit) {
      setSelectedClientId(undefined);
    }
  }, [invoiceData?.clientId, isEdit]);

  const configWithOptions = useMemo(() => {
    const fields = moduleConfig.fields.map((field) => {
      if (field.name === 'clientId') {
        return {
          ...field,
          type: 'select' as const,
          label: 'Client',
          placeholder: 'Select a client',
          required: true,
          options: [],
          apiEndpoint: '/crm-clients',
          labelField: 'clientName',
          valueField: 'clientId' as const,
        };
      }
      if (field.name === 'supportAgreementId') {
        return {
          ...field,
          type: 'select' as const,
          label: 'Support Agreement (Optional)',
          placeholder: selectedClientId ? 'Select a support agreement (optional)' : 'Select a client first',
          options: [{ value: '', label: 'None' }, ...supportAgreementOptions],
          disabled: !selectedClientId,
        };
      }
      if (field.name === 'invoiceType') {
        return {
          ...field,
          type: 'select' as const,
          label: 'Invoice Type',
          placeholder: 'Select invoice type',
          required: true,
          options: invoiceTypeOptions,
          valueField: 'id' as const,
        };
      }
      if (field.name === 'InvoiceCategoryId') {
        return {
          ...field,
          type: 'select' as const,
          label: 'Invoice Category',
          placeholder: 'Select invoice category (optional)',
          options: [{ value: '', label: 'None' }],
          apiEndpoint: '/p-crm-invoice-category',
          labelField: 'pic_descr',
          valueField: 'id' as const,
        };
      }
      return field;
    });

    // Form data allows foreign keys as string for select components
    type FormClientInvoice = Omit<ClientInvoice, 'clientId' | 'invoiceType' | 'InvoiceCategoryId' | 'supportAgreementId'> & {
      clientId?: string | number;
      invoiceType?: string | number;
      InvoiceCategoryId?: string | number;
      supportAgreementId?: string | number;
    };

    // Custom mapItemToFormData that converts foreign keys to string for select
    const customMapItemToFormData = (item: ClientInvoice): Partial<FormClientInvoice> => {
      const baseData = mapItemToFormData(item);
      return {
        ...baseData,
        clientId: baseData.clientId ? String(baseData.clientId) : '',
        invoiceType: baseData.invoiceType ? String(baseData.invoiceType) : '',
        InvoiceCategoryId: baseData.InvoiceCategoryId ? String(baseData.InvoiceCategoryId) : '',
        supportAgreementId: baseData.supportAgreementId ? String(baseData.supportAgreementId) : '',
      };
    };

    const customGetInitialFormData = (): Partial<FormClientInvoice> => {
      return {
        ...getInitialFormData(),
        clientId: '',
        invoiceType: '',
        InvoiceCategoryId: '',
        supportAgreementId: '',
      };
    };

    // Convert string back to number before sending to API
    const customCreate = async (data: Partial<FormClientInvoice>, files?: File[]) => {
      const processedData: Partial<ClientInvoice> = {
        ...data,
        clientId: (data.clientId !== undefined && data.clientId !== null && String(data.clientId) !== '')
          ? Number(data.clientId)
          : undefined,
        invoiceType: (data.invoiceType !== undefined && data.invoiceType !== null && String(data.invoiceType) !== '')
          ? Number(data.invoiceType)
          : undefined,
        InvoiceCategoryId: (data.InvoiceCategoryId !== undefined && data.InvoiceCategoryId !== null && String(data.InvoiceCategoryId) !== '')
          ? Number(data.InvoiceCategoryId)
          : undefined,
        supportAgreementId: (data.supportAgreementId !== undefined && data.supportAgreementId !== null && String(data.supportAgreementId) !== '')
          ? Number(data.supportAgreementId)
          : undefined,
      };
      return await moduleConfig.form.create(processedData, files);
    };

    const customUpdate = async (id: number, data: Partial<FormClientInvoice>, files?: File[], filesToDetach?: number[]) => {
      const processedData: Partial<ClientInvoice> = {
        ...data,
        clientId: (data.clientId !== undefined && data.clientId !== null && String(data.clientId) !== '')
          ? Number(data.clientId)
          : undefined,
        invoiceType: (data.invoiceType !== undefined && data.invoiceType !== null && String(data.invoiceType) !== '')
          ? Number(data.invoiceType)
          : undefined,
        InvoiceCategoryId: (data.InvoiceCategoryId !== undefined && data.InvoiceCategoryId !== null && String(data.InvoiceCategoryId) !== '')
          ? Number(data.InvoiceCategoryId)
          : undefined,
        supportAgreementId: (data.supportAgreementId !== undefined && data.supportAgreementId !== null && String(data.supportAgreementId) !== '')
          ? Number(data.supportAgreementId)
          : undefined,
      };
      return await moduleConfig.form.update(id, processedData, files, filesToDetach);
    };

    return {
      ...moduleConfig,
      fields,
      getInitialFormData: customGetInitialFormData as () => Partial<ClientInvoice>,
      mapItemToFormData: customMapItemToFormData as (item: ClientInvoice) => Partial<ClientInvoice>,
      form: {
        ...moduleConfig.form,
        create: customCreate as (data: Partial<ClientInvoice>, files?: File[]) => Promise<ClientInvoice>,
        update: customUpdate as (id: number, data: Partial<ClientInvoice>, files?: File[], filesToDetach?: number[]) => Promise<ClientInvoice>,
      },
    };
  }, [supportAgreementOptions, selectedClientId, invoiceTypeOptions]);

  return (
    <GenericFormPage<ClientInvoice>
      useData={useClientInvoice}
      config={configWithOptions}
    />
  );
};

export default Form;
